/**
 * Parse a YYYY-MM-DD string into a Date object.
 */
export function parseDate(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a Date object to YYYY-MM-DD.
 */
export function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Get the month key for grouping: "YYYY-MM"
 */
export function getMonthKey(dateStr) {
  return dateStr.slice(0, 7);
}

/**
 * Get a display label for a month key: "January 2024"
 */
export function getMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/**
 * Get the year key: "YYYY"
 */
export function getYearKey(dateStr) {
  return dateStr.slice(0, 4);
}

/**
 * Generate all month keys between start and end dates (inclusive).
 */
export function getMonthKeysBetween(startDateStr, endDateStr) {
  const start = parseDate(startDateStr);
  const end = parseDate(endDateStr);
  const keys = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  while (current <= end) {
    keys.push(formatDate(current).slice(0, 7));
    current.setMonth(current.getMonth() + 1);
  }
  return keys;
}

/**
 * Calculate how many months an event spans within a given month key.
 * Returns a fraction (0 to 1) representing the portion of the month covered.
 */
export function getMonthCoverage(event, monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const monthStart = new Date(y, m - 1, 1);
  const monthEnd = new Date(y, m, 0); // last day of month
  const daysInMonth = monthEnd.getDate();

  const eventStart = parseDate(event.startDate);
  const eventEnd = event.type === "range" && event.endDate ? parseDate(event.endDate) : eventStart;

  if (eventEnd < monthStart || eventStart > monthEnd) return 0;
  if (event.type === "punctual") return 1;

  const coverStart = eventStart > monthStart ? eventStart : monthStart;
  const coverEnd = eventEnd < monthEnd ? eventEnd : monthEnd;

  const days = Math.floor((coverEnd - coverStart) / (1000 * 60 * 60 * 24)) + 1;
  return Math.min(days / daysInMonth, 1);
}

/**
 * Sort events by start date.
 */
export function sortEvents(events) {
  return [...events].sort((a, b) => {
    if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
    // Range events before punctual, longer ranges after shorter
    const aIsRange = a.type === "range";
    const bIsRange = b.type === "range";
    if (aIsRange && !bIsRange) return -1;
    if (!aIsRange && bIsRange) return 1;
    return a.title.localeCompare(b.title);
  });
}
